import React, { useState, useRef, useEffect } from 'react';
import { Upload, FileText, ChevronLeft, ChevronRight, Edit2, Save, X, Check, AlertCircle, Loader2, CheckCircle, XCircle, Info } from 'lucide-react';
const NewOnboarding = () => {
  function normalizeData(data) {
    // Step 1: collect all unique keys from all employees
    const allKeys = new Set();
    for (const empId in data) {
      Object.keys(data[empId]).forEach(key => allKeys.add(key));
    }
  
    // Step 2: add missing keys with false
    const result = {};
    for (const empId in data) {
      result[empId] = {};
      allKeys.forEach(key => {
        result[empId][key] = data[empId][key] || false;
      });
    }
  
    return result;
  }
  const [step, setStep] = useState(1);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [isEditing, setIsEditing] = useState({});
  const [editedData, setEditedData] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [toasts, setToasts] = useState([]);
  const fileInputRef = useRef(null);
  const [lastFile, setLastFile] = useState(null);


  // Toast management
  const showToast = (message, type = 'info') => {
    console.log(message, type);
    const id = Date.now();
    const toast = { id, message, type };
    setToasts(prev => [...prev, toast]);
    
    // Auto remove toast after 5 seconds
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 5000);
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  // Toast component
  const ToastContainer = () => (
    <div className="fixed top-4 right-4 z-50 space-y-2">
      {toasts.map(toast => (
        <div
          key={toast.id}
          className={`flex items-center p-4 rounded-lg shadow-lg max-w-sm animate-slide-in ${
            toast.type === 'success' ? 'bg-green-100 text-green-800 border border-green-200' :
            toast.type === 'error' ? 'bg-red-100 text-red-800 border border-red-200' :
            toast.type === 'warning' ? 'bg-yellow-100 text-yellow-800 border border-yellow-200' :
            'bg-blue-100 text-blue-800 border border-blue-200'
          }`}
        >
          <div className="flex items-center">
            {toast.type === 'success' && <CheckCircle size={20} className="mr-2" />}
            {toast.type === 'error' && <XCircle size={20} className="mr-2" />}
            {toast.type === 'warning' && <AlertCircle size={20} className="mr-2" />}
            {toast.type === 'info' && <Info size={20} className="mr-2" />}
            <span className="flex-1">{toast.message}</span>
          </div>
          <button
            onClick={() => removeToast(toast.id)}
            className="ml-4 text-current opacity-70 hover:opacity-100"
          >
            <X size={16} />
          </button>
        </div>
      ))}
    </div>
  );

  // Mock data for demonstration - in real app this comes from backend
  const mockPageData = [
    {
      pageNumber: 1,
      description: "Personal Information Page - Contains employee basic details, father/mother name, DOB, and personal identifiers",
      extractedData: ["Name: Jivan Sudhakar Phatale", "DOB: 1986-03-24", "Father: Sudhakar Rayaji Phatale"]
    },
    {
      pageNumber: 2,
      description: "Address and Contact Information - Permanent address details and contact information",
      extractedData: ["Address: Parbhani, Maharashtra - 431401", "Phone: 8411022883", "Email: j.s.phatale@gmail.com"]
    },
    {
      pageNumber: 3,
      description: "Employment Details - Job designation, joining date, and employment specifics",
      extractedData: ["Designation: Sr. Area Manager", "Grade: D3", "DOJ: 2022-05-16", "Location: Nanded"]
    },
    {
      pageNumber: 4,
      description: "Bank and Salary Information - Banking details and compensation structure",
      extractedData: ["Bank: HDFC Bank", "Account: 50100000337541", "CTC: ₹922,160.04 annually"]
    }
  ];

  const [employeeData, setEmployeeData] = useState({
    eid: "00303739",
    name: "Jivan Sudhakar Phatale",
    personal_information: {
      employee_name: "Jivan Sudhakar Phatale",
      father_name: "Sudhakar Rayaji Phatale",
      mother_name: "Kausalya Sudhakar Phatale",
      date_of_birth: "1986-03-24",
      place_of_birth: "Parola MH",
      gender: "male",
      nationality: "Indian",
      religion: "Hindu",
      marital_status: "married",
      blood_group: "O+ positive",
      languages_known: ["Marathi", "Hindi", "English"]
    },
    address_information: {
      permanent_address: {
        village: "Parbhani",
        post: "Parbhani",
        district: "Parbhani",
        state: "Maharashtra",
        pin_code: "431401"
      }
    },
    contact_information: {
      phone_number: "8411022883",
      email: "j.s.phatale@gmail.com",
      emergency_contact: {
        name: "Sudhakar R. Phatale",
        mobile: "9922381144",
        email: "j.s.phatale@gmail.com"
      }
    },
    identification_documents: {
      aadhaar_number: "474826024687",
      pan_number: "BGOPP1775L"
    },
    employment_details: {
      designation: "Sr. Area Manager",
      grade: "D3",
      eid: "00303739",
      uan_number: "",
      date_of_joining: "2022-05-16",
      date_of_appointment: "2022-04-22",
      location: "Nanded",
      division: "Ceramics",
      reporting_manager: "",
      source_of_candidate: "Direct"
    },
    salary_and_compensation: {
      ctc_annual: 922160.04,
      basic_pay: { monthly: 18000 },
      house_rent: { monthly: 9000 },
      special_allowance: { monthly: 16820 },
      performance_allowance: { monthly: 16000 },
      bonus_exgratia: { monthly: 3600 },
      pf_employer_contribution: { monthly: 2160 }
    },
    bank_details: {
      bank_name: "HDFC Bank",
      account_number: "50100000337541",
      ifsc_code: "HDFC0001789"
    },
    nominee_information: [{
      nominee_name: "Swati Jnan Phatale",
      relationship: "Spouse",
      nominee_age: 31,
      nominee_dob: "1990-06-08",
      share_percentage: 100
    }],
    declarations_and_compliance: {
      physical_disabilities: "No",
      major_illness: "No",
      court_cases: "No",
      police_custody: "No"
    },
    education: {
      higher_education: "B.Sc. from B.A.M.U Aurangabad, 2012"
    }
  });

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    
    if (files.length === 0) return;

    // Validate file types
    const invalidFiles = files.filter(file => !file.type.includes('pdf'));
    if (invalidFiles.length > 0) {
      showToast('Please select only PDF files', 'error');
      return;
    }

    // Validate file sizes (max 10MB per file)
    const oversizedFiles = files.filter(file => file.size > 10 * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      showToast('File size should not exceed 10MB', 'error');
      return;
    }

    setIsUploading(true);
    showToast(`Uploading ${files.length} file(s)...`, 'info');

    try {
      // Simulate file upload delay
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      setUploadedFiles(prev => [...prev, ...files]);
      showToast(`Successfully uploaded ${files.length} file(s)`, 'success');
    } catch (error) {
      showToast('Failed to upload files. Please try again.', 'error');
    } finally {
      setIsUploading(false);
      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const removeFile = (index) => {
    const fileName = uploadedFiles[index].name;
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
    showToast(`Removed ${fileName}`, 'info');
  };

  const processDocuments = async () => {
    if (uploadedFiles.length === 0) {
      showToast('Please upload at least one PDF file', 'warning');
      return;
    }

    setIsProcessing(true);
    showToast('Processing documents and extracting data...', 'info');

    try {
      // Simulate backend processing
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      showToast('Documents processed successfully! Review the extracted data.', 'success');
      setStep(2);
    } catch (error) {
      showToast('Failed to process documents. Please try again.', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  const startEditing = (section, field) => {
    const key = `${section}.${field}`;
    setIsEditing(prev => ({ ...prev, [key]: true }));
    
    // Deep clone the current value for editing
    const value = field.includes('.') 
      ? field.split('.').reduce((obj, key) => obj?.[key], employeeData[section])
      : employeeData[section][field];
    
    setEditedData(prev => ({ ...prev, [key]: value }));
  };

  const saveEdit = (section, field) => {
    const key = `${section}.${field}`;
    const newValue = editedData[key];
    
    if (!newValue || newValue.trim() === '') {
      showToast('Field cannot be empty', 'warning');
      return;
    }
    
    setEmployeeData(prev => {
      const updated = { ...prev };
      if (field.includes('.')) {
        const keys = field.split('.');
        let current = updated[section];
        for (let i = 0; i < keys.length - 1; i++) {
          current = current[keys[i]];
        }
        current[keys[keys.length - 1]] = newValue;
      } else {
        updated[section][field] = newValue;
      }
      return updated;
    });
    
    setIsEditing(prev => ({ ...prev, [key]: false }));
    showToast('Field updated successfully', 'success');
  };

  const cancelEdit = (section, field) => {
    const key = `${section}.${field}`;
    setIsEditing(prev => ({ ...prev, [key]: false }));
    setEditedData(prev => {
      const updated = { ...prev };
      delete updated[key];
      return updated;
    });
  };
  const handleUploadDocument = async (file) => {
    try {
      // Prepare form data
      const formData = new FormData();
      formData.append("file", file);
  
      console.log("Uploading file:", {
        employeeId: null,
        documentType: null,
        filename: file.name,
        size: file.size,
        type: file.type,
      });
  
      // Call API (replace with your actual endpoint)
      const response = await fetch(`${process.env.REACT_APP_PUBLIC_API_BASE_URL}/v1/employees/upload-document`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
        body: formData,
      });
  
      if (!response.ok) {
        throw new Error("Upload failed");
      }
  
      const result = await response.json();
      console.log("Upload success:", result);
    } catch (error) {
      console.error("Error uploading file:", error);
    }
  };

  const handleAccept = async () => {
    try {
      showToast('Saving employee data...', 'info');
      
      // Simulate API call to save data
handleUploadDocument(uploadedFiles[0])
      
      showToast('Onboarding completed successfully! Employee data has been saved.', 'success');
      setStep(1);
      setUploadedFiles([]);
      setCurrentPageIndex(0);
      setIsEditing({});
      setEditedData({});
      console.log('Final employee data:', employeeData);
      
      // Optional: Reset form or redirect
      showToast('Redirecting to employee dashboard...', 'info');
    } catch (error) {
      showToast('Failed to save employee data. Please try again.', 'error');
    }
  };

  const handleDeny = () => {
    if (window.confirm('Are you sure you want to deny this onboarding? This action cannot be undone.')) {
      showToast('Onboarding has been denied and dropped.', 'warning');
      setStep(1);
      setUploadedFiles([]);
      setCurrentPageIndex(0);
      setIsEditing({});
      setEditedData({});
    }
  };

  const renderEditableField = (section, field, label, value, type = 'text') => {
    const key = `${section}.${field}`;
    const editing = isEditing[key];
    
    if (Array.isArray(value)) {
      return (
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
          <div className="flex flex-wrap gap-2">
            {value.map((item, index) => (
              <span key={index} className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm">
                {item}
              </span>
            ))}
          </div>
        </div>
      );
    }

    return (
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
        <div className="flex items-center gap-2">
          {editing ? (
            <>
              <input
                type={type}
                value={editedData[key] || ''}
                onChange={(e) => setEditedData(prev => ({ ...prev, [key]: e.target.value }))}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                onClick={() => saveEdit(section, field)}
                className="p-2 text-green-600 hover:bg-green-50 rounded"
              >
                <Save size={16} />
              </button>
              <button
                onClick={() => cancelEdit(section, field)}
                className="p-2 text-red-600 hover:bg-red-50 rounded"
              >
                <X size={16} />
              </button>
            </>
          ) : (
            <>
              <span className="flex-1 px-3 py-2 bg-gray-50 rounded border text-gray-900">
                {value || 'Not specified'}
              </span>
              <button
                onClick={() => startEditing(section, field)}
                className="p-2 text-blue-600 hover:bg-blue-50 rounded"
              >
                <Edit2 size={16} />
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  if (step === 1) {
    return (
      <div className="max-w-4xl mx-auto p-6 bg-white">
        <ToastContainer />
        
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Employee Onboarding</h1>
          <p className="text-gray-600">Upload employee documents to begin the onboarding process</p>
        </div>

        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center mb-6">
          <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Upload PDF Documents</h3>
          <p className="text-gray-500 mb-4">Select single or multiple PDF files (max 10MB each)</p>
          
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf"
            onChange={handleFileUpload}
            className="hidden"
            disabled={isUploading}
          />
          
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center mx-auto"
          >
            {isUploading ? (
              <>
                <Loader2 className="animate-spin h-4 w-4 mr-2" />
                Uploading...
              </>
            ) : (
              'Choose PDF Files'
            )}
          </button>
        </div>

        {uploadedFiles.length > 0 && (
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Uploaded Files ({uploadedFiles.length})
            </h3>
            <div className="space-y-2">
              {uploadedFiles.map((file, index) => (
                <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded-md">
                  <div className="flex items-center">
                    <FileText className="h-5 w-5 text-red-500 mr-2" />
                    <span className="text-sm text-gray-700">{file.name}</span>
                    <span className="text-xs text-gray-500 ml-2">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                  </div>
                  <button
                    onClick={() => removeFile(index)}
                    disabled={isUploading}
                    className="text-red-600 hover:bg-red-50 p-1 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <X size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="text-center">
          <button
            onClick={processDocuments}
            disabled={uploadedFiles.length === 0 || isUploading || isProcessing}
            className="bg-green-600 text-white px-8 py-3 rounded-md hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center mx-auto"
          >
            {isProcessing ? (
              <>
                <Loader2 className="animate-spin h-5 w-5 mr-2" />
                Processing Documents...
              </>
            ) : (
              'Process Documents'
            )}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6 bg-white">
        <ToastContainer />
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Review Extracted Data</h1>
        <p className="text-gray-600">Verify and edit the information extracted from uploaded documents</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* PDF Page Navigation */}
        {/* <div className="bg-gray-50 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Document Pages</h2>
          
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => setCurrentPageIndex(Math.max(0, currentPageIndex - 1))}
              disabled={currentPageIndex === 0}
              className="flex items-center px-3 py-2 bg-white border rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={16} className="mr-1" />
              Previous
            </button>
            
            <span className="text-sm text-gray-600">
              Page {currentPageIndex + 1} of {mockPageData.length}
            </span>
            
            <button
              onClick={() => setCurrentPageIndex(Math.min(mockPageData.length - 1, currentPageIndex + 1))}
              disabled={currentPageIndex === mockPageData.length - 1}
              className="flex items-center px-3 py-2 bg-white border rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
              <ChevronRight size={16} className="ml-1" />
            </button>
          </div>

          <div className="bg-white rounded-md p-4 border">
            <h3 className="font-medium text-gray-900 mb-2">
              Page {mockPageData[currentPageIndex].pageNumber}
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              {mockPageData[currentPageIndex].description}
            </p>
            
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Extracted Data:</h4>
              <ul className="space-y-1">
                {mockPageData[currentPageIndex].extractedData.map((item, index) => (
                  <li key={index} className="text-sm text-gray-600 flex items-start">
                    <span className="w-2 h-2 bg-blue-400 rounded-full mr-2 mt-2 flex-shrink-0"></span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div> */}

        {/* Employee Data Form */}
        <div className="space-y-6 h-[calc(100vh-200px)] overflow-auto">
          <div className="bg-white border rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Personal Information</h3>
            {renderEditableField('personal_information', 'employee_name', 'Employee Name', employeeData.personal_information.employee_name)}
            {renderEditableField('personal_information', 'father_name', 'Father Name', employeeData.personal_information.father_name)}
            {renderEditableField('personal_information', 'mother_name', 'Mother Name', employeeData.personal_information.mother_name)}
            {renderEditableField('personal_information', 'date_of_birth', 'Date of Birth', employeeData.personal_information.date_of_birth, 'date')}
            {renderEditableField('personal_information', 'gender', 'Gender', employeeData.personal_information.gender)}
            {renderEditableField('personal_information', 'nationality', 'Nationality', employeeData.personal_information.nationality)}
            {renderEditableField('personal_information', 'languages_known', 'Languages Known', employeeData.personal_information.languages_known)}
          </div>

          <div className="bg-white border rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
            {renderEditableField('contact_information', 'phone_number', 'Phone Number', employeeData.contact_information.phone_number)}
            {renderEditableField('contact_information', 'email', 'Email', employeeData.contact_information.email, 'email')}
            {renderEditableField('contact_information', 'emergency_contact.name', 'Emergency Contact Name', employeeData.contact_information.emergency_contact.name)}
            {renderEditableField('contact_information', 'emergency_contact.mobile', 'Emergency Contact Mobile', employeeData.contact_information.emergency_contact.mobile)}
          </div>

          <div className="bg-white border rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Employment Details</h3>
            {renderEditableField('employment_details', 'designation', 'Designation', employeeData.employment_details.designation)}
            {renderEditableField('employment_details', 'grade', 'Grade', employeeData.employment_details.grade)}
            {renderEditableField('employment_details', 'date_of_joining', 'Date of Joining', employeeData.employment_details.date_of_joining, 'date')}
            {renderEditableField('employment_details', 'location', 'Location', employeeData.employment_details.location)}
            {renderEditableField('employment_details', 'division', 'Division', employeeData.employment_details.division)}
          </div>

          <div className="bg-white border rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Bank Details</h3>
            {renderEditableField('bank_details', 'bank_name', 'Bank Name', employeeData.bank_details.bank_name)}
            {renderEditableField('bank_details', 'account_number', 'Account Number', employeeData.bank_details.account_number)}
            {renderEditableField('bank_details', 'ifsc_code', 'IFSC Code', employeeData.bank_details.ifsc_code)}
          </div>

          <div className="bg-white border rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Salary Information</h3>
            {renderEditableField('salary_and_compensation', 'ctc_annual', 'Annual CTC', employeeData.salary_and_compensation.ctc_annual, 'number')}
            {renderEditableField('salary_and_compensation', 'basic_pay.monthly', 'Monthly Basic Pay', employeeData.salary_and_compensation.basic_pay.monthly, 'number')}
            {renderEditableField('salary_and_compensation', 'house_rent.monthly', 'Monthly House Rent', employeeData.salary_and_compensation.house_rent.monthly, 'number')}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="mt-8 flex justify-center space-x-4">
        <button
          onClick={handleDeny}
          className="flex items-center px-6 py-3 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
        >
          <X size={16} className="mr-2" />
          Deny Onboarding
        </button>
        <button
          onClick={handleAccept}
          className="flex items-center px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
        >
          <Check size={16} className="mr-2" />
          Accept & Complete Onboarding
        </button>
      </div>

      <div className="mt-6 p-4 bg-blue-50 rounded-md">
        <div className="flex items-start">
          <AlertCircle className="h-5 w-5 text-blue-600 mr-2 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium">Review Instructions:</p>
            <ul className="mt-1 list-disc list-inside space-y-1">
              <li>Use the page slider to review extracted data from each document page</li>
              <li>Click the edit icon next to any field to modify incorrect information</li>
              <li>All changes are saved automatically when you click the save button</li>
              <li>Accept to complete onboarding or deny to drop the process</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewOnboarding;